import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import PageHeader from '../../Common/PageHeader';
import Icons from '../../../../components/Common/Icons/Icons';
import Footer from '../../Layout/Footer';
import DatePickerWithRange from '../../../../components/Common/UI/DateRange';
import AccordionTable from '../../Common/AccordionTable';
import { Input } from '../../../../components/Common/UI/Input';
import { initialPeriod, IPeriod } from '../Notices';
import { ITaxesData, taxesAccordion, taxesCol, SEARCH_SUGGESTIONS } from './Data';

function Taxes() {
  const [active, setActive] = useState<number | undefined>(0);
  const [selectedPeriod, setSelectedPeriod] = useState<IPeriod>(initialPeriod);
  const [searchVal, setSearchVal] = useState('');
  const [open, setOpen] = useState(false);

  const handleOnChange = (from?: Date, to?: Date) => {
    if (from && to) {
      setSelectedPeriod({
        from: dayjs(from).format('MMM DD YYYY'),
        to: dayjs(to).format('MMM DD YYYY'),
      });
    }
  };

  const handleActive = (index?: number) => {
    setActive(index);
  };

  const filtered = useMemo(() => {
    if (!searchVal.trim()) return [];

    return SEARCH_SUGGESTIONS.filter(item => item.toLowerCase().includes(searchVal.toLowerCase()));
  }, [searchVal]);

  const taxesData = useMemo(() => {
    let filteredGroups = taxesAccordion;

    if (!(selectedPeriod.from === initialPeriod.from && selectedPeriod.to === initialPeriod.to)) {
      filteredGroups = taxesAccordion.filter(({ year }) =>
        dayjs(`01-01-${year}`).isBetween(dayjs(selectedPeriod.from), dayjs(selectedPeriod.to), 'year', '[]'),
      );
    }
    if (searchVal.trim()) {
      const searchLower = searchVal.toLowerCase();

      filteredGroups = filteredGroups
        .map(group => ({
          ...group,
          data: group.data.filter(item => {
            if (searchLower === 'payroll') return item.type === 'Payroll';

            return item.form.toLowerCase().includes(searchLower) || item.date.toLowerCase().includes(searchLower);
          }),
        }))
        .filter(group => group.data.length > 0);
    } else {
      filteredGroups = filteredGroups
        .map(group => ({
          ...group,
          data: group.data.filter(item => !item.type),
        }))
        .filter(group => group.data.length > 0);
    }

    return filteredGroups;
  }, [selectedPeriod, searchVal]);

  return (
    <div>
      <PageHeader
        icon={<Icons.Tax />}
        title="eTax"
        actions={
          <div className="flex items-center gap-4">
            <div className="relative max-w-60">
              <Input
                placeholder="Search"
                variant="home"
                inputClassName="border-[#FFFFFF]"
                value={searchVal}
                icon={<Icons.Search />}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                onChange={e => {
                  setSearchVal(e.target.value);
                  setOpen(!!e.target.value);
                }}
              />

              {open && searchVal.trim() && filtered.length > 0 && (
                <div className="absolute z-50 mt-1 w-full rounded-md bg-white shadow">
                  {filtered.map(item => (
                    <div
                      key={item}
                      tabIndex={0}
                      role="button"
                      className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100"
                      onMouseDown={() => {
                        setSearchVal(item);
                        setOpen(false);
                      }}
                    >
                      {item}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DatePickerWithRange
              variant="transparent"
              from={dayjs(initialPeriod.from).toDate()}
              to={dayjs(initialPeriod.to).toDate()}
              handleOnChange={handleOnChange}
            />
          </div>
        }
      />
      <div className="px-[20px] grid gap-[20px]">
        {(taxesData || []).length ? (
          <>
            <AccordionTable<ITaxesData>
              data={taxesData}
              tableCol={taxesCol}
              handleActive={handleActive}
              active={active}
            />
            <Footer />
          </>
        ) : (
          <div className="font-bold text-[18px] text-center my-[100px]">
            There is no statement available for this data range.
          </div>
        )}
      </div>
    </div>
  );
}

export default Taxes;
